export interface NavItem {
  label: string
  to: string
}

export const NAV_ITEMS: NavItem[] = [
  { label: 'Home', to: '/' },
  { label: 'Energy', to: '/energy' },
  { label: 'Preparedness', to: '/preparedness' },
  { label: 'Water', to: '/water' },
  { label: 'Smart Home', to: '/smart-home' },
  { label: 'Buying Guides', to: '/buying-guides' },
  { label: 'Reviews', to: '/reviews' },
  { label: 'Research', to: '/research' },
  { label: 'About', to: '/about' },
  { label: 'Contact', to: '/contact' },
]

export const FOOTER_LINKS: NavItem[] = [
  { label: 'Trust Framework', to: '/agreexa-trust-framework' },
  { label: 'Editorial Standards', to: '/agreexa-trust-framework' },
  { label: 'Affiliate Transparency', to: '/agreexa-trust-framework' },
  { label: 'Privacy', to: '/contact' },
  { label: 'Terms', to: '/contact' },
  { label: 'Contact', to: '/contact' },
]

export interface FrameworkStep {
  number: string
  title: string
  description: string
}

export const FRAMEWORK_STEPS: FrameworkStep[] = [
  {
    number: '01',
    title: 'Research',
    description:
      'We map the category, identify credible manufacturers and gather primary specifications, standards and independent test data.',
  },
  {
    number: '02',
    title: 'Analysis',
    description:
      'Analysts compare products against measurable criteria — efficiency, durability, safety and real-world usability.',
  },
  {
    number: '03',
    title: 'Verification',
    description:
      'Claims are cross-checked against certifications, warranty terms and long-term field reports before anything is scored.',
  },
  {
    number: '04',
    title: 'Trust Score',
    description:
      'Each product receives an Agreexa Trust Score® across seven weighted dimensions, calculated with a consistent rubric.',
  },
  {
    number: '05',
    title: 'Editorial Approval',
    description:
      'A senior editor reviews the evidence, checks for conflicts of interest and signs off on the recommendation.',
  },
  {
    number: '06',
    title: 'Publication',
    description:
      'Only after full approval is a review published — with its methodology, sources and affiliate disclosures visible.',
  },
]

export interface TrustDimension {
  label: string
  score: number
}

export interface Review {
  slug: string
  category: string
  title: string
  product: string
  excerpt: string
  image: string
  trustScore: number
  recommendation: string
  updated: string
  dimensions: TrustDimension[]
}

export const REVIEWS: Review[] = [
  {
    slug: 'ecoflow-delta-pro',
    category: 'Energy',
    title: 'A Whole-Home Battery That Earns Its Place',
    product: 'EcoFlow DELTA Pro Portable Power Station',
    excerpt:
      'A capable home backup system with expandable capacity, transparent cycle ratings and dependable support — evaluated over three months of daily use.',
    image:
      'https://images.unsplash.com/photo-1620714223084-8fcacc6dfd8d?auto=format&fit=crop&w=1200&q=80',
    trustScore: 94,
    recommendation: 'Highly Recommended',
    updated: 'June 2026',
    dimensions: [
      { label: 'Performance', score: 96 },
      { label: 'Reliability', score: 95 },
      { label: 'Ease of Use', score: 92 },
      { label: 'Customer Support', score: 90 },
      { label: 'Transparency', score: 97 },
      { label: 'Value', score: 91 },
      { label: 'Long-Term Ownership', score: 93 },
    ],
  },
  {
    slug: 'berkey-gravity-filter',
    category: 'Water',
    title: 'Off-Grid Water Filtration, Independently Verified',
    product: 'Gravity-Fed Countertop Water Filtration System',
    excerpt:
      'We measured flow rate, contaminant reduction and replacement economics against certified lab data to see how it holds up in a real household.',
    image:
      'https://images.unsplash.com/photo-1548839140-29a749e1cf4d?auto=format&fit=crop&w=1200&q=80',
    trustScore: 88,
    recommendation: 'Recommended',
    updated: 'May 2026',
    dimensions: [
      { label: 'Performance', score: 90 },
      { label: 'Reliability', score: 89 },
      { label: 'Ease of Use', score: 86 },
      { label: 'Customer Support', score: 84 },
      { label: 'Transparency', score: 88 },
      { label: 'Value', score: 90 },
      { label: 'Long-Term Ownership', score: 87 },
    ],
  },
  {
    slug: 'smart-energy-monitor',
    category: 'Smart Home',
    title: 'Knowing Every Watt: A Whole-Home Energy Monitor',
    product: 'Whole-Home Smart Energy Monitor',
    excerpt:
      'Granular circuit-level insight with a clear app and honest data privacy terms. We assessed accuracy against a calibrated reference meter.',
    image:
      'https://images.unsplash.com/photo-1558002038-1055907df827?auto=format&fit=crop&w=1200&q=80',
    trustScore: 91,
    recommendation: 'Highly Recommended',
    updated: 'June 2026',
    dimensions: [
      { label: 'Performance', score: 93 },
      { label: 'Reliability', score: 92 },
      { label: 'Ease of Use', score: 90 },
      { label: 'Customer Support', score: 88 },
      { label: 'Transparency', score: 94 },
      { label: 'Value', score: 89 },
      { label: 'Long-Term Ownership', score: 90 },
    ],
  },
]

export interface Guide {
  slug: string
  category: string
  title: string
  excerpt: string
  image: string
  readTime: string
}

export const GUIDES: Guide[] = [
  {
    slug: 'home-battery-backup',
    category: 'Energy',
    title: 'The Best Home Battery Backup Systems',
    excerpt:
      'How to size, compare and choose a battery backup that keeps the essentials running through any outage.',
    image:
      'https://images.unsplash.com/photo-1509391366360-2e959784a276?auto=format&fit=crop&w=1200&q=80',
    readTime: '18 min read',
  },
  {
    slug: 'emergency-preparedness-kit',
    category: 'Preparedness',
    title: 'Building a Two-Week Home Preparedness Kit',
    excerpt:
      'A practical, evidence-led approach to food, power, water and communications for real emergencies.',
    image:
      'https://images.unsplash.com/photo-1587049352846-4a222e784d38?auto=format&fit=crop&w=1200&q=80',
    readTime: '22 min read',
  },
  {
    slug: 'whole-house-water',
    category: 'Water',
    title: 'Whole-House Water Filtration, Explained',
    excerpt:
      'From sediment to contaminants — what actually matters when filtering water for an independent home.',
    image:
      'https://images.unsplash.com/photo-1563453392212-326f5e854473?auto=format&fit=crop&w=1200&q=80',
    readTime: '15 min read',
  },
  {
    slug: 'solar-for-beginners',
    category: 'Energy',
    title: 'Residential Solar: A Buyer’s First Guide',
    excerpt:
      'Panels, inverters, incentives and payback periods — a clear starting point for going solar.',
    image:
      'https://images.unsplash.com/photo-1508514177221-188b1cf16e9d?auto=format&fit=crop&w=1200&q=80',
    readTime: '20 min read',
  },
  {
    slug: 'smart-thermostats',
    category: 'Smart Home',
    title: 'The Best Smart Thermostats for Efficiency',
    excerpt:
      'Which thermostats genuinely cut energy use — and which just add screens to your wall.',
    image:
      'https://images.unsplash.com/photo-1567925086744-4e0da5e79b96?auto=format&fit=crop&w=1200&q=80',
    readTime: '12 min read',
  },
  {
    slug: 'backup-generators',
    category: 'Preparedness',
    title: 'Standby vs. Portable Generators',
    excerpt:
      'A transparent comparison of fuel, capacity, noise and total cost of ownership.',
    image:
      'https://images.unsplash.com/photo-1621905251189-08b45d6a269e?auto=format&fit=crop&w=1200&q=80',
    readTime: '16 min read',
  },
]

export interface Category {
  slug: string
  name: string
  headline: string
  description: string
  image: string
}

export const CATEGORIES: Record<string, Category> = {
  energy: {
    slug: 'energy',
    name: 'Energy',
    headline: 'Energy Independence',
    description:
      'Solar, battery storage, backup power and efficiency — independently researched to help your home rely less on the grid.',
    image:
      'https://images.unsplash.com/photo-1509391366360-2e959784a276?auto=format&fit=crop&w=1600&q=80',
  },
  preparedness: {
    slug: 'preparedness',
    name: 'Preparedness',
    headline: 'Emergency Preparedness',
    description:
      'Evidence-based guidance on power, food, water and communications so your household is ready for the unexpected.',
    image:
      'https://images.unsplash.com/photo-1587049352846-4a222e784d38?auto=format&fit=crop&w=1600&q=80',
  },
  water: {
    slug: 'water',
    name: 'Water',
    headline: 'Water Independence',
    description:
      'Filtration, storage and quality testing — verified against certified data so you can trust what comes out of the tap.',
    image:
      'https://images.unsplash.com/photo-1563453392212-326f5e854473?auto=format&fit=crop&w=1600&q=80',
  },
  'smart-home': {
    slug: 'smart-home',
    name: 'Smart Home',
    headline: 'Smart Home Technology',
    description:
      'Monitoring, automation and control that make an independent home more efficient — reviewed for accuracy and privacy.',
    image:
      'https://images.unsplash.com/photo-1558002038-1055907df827?auto=format&fit=crop&w=1600&q=80',
  },
}

export interface EcosystemPublication {
  name: string
  status: 'live' | 'planned'
}

export const FUTURE_PUBLICATIONS: EcosystemPublication[] = [
  { name: 'Smart Home Hub', status: 'planned' },
  { name: 'Preparedness Hub', status: 'planned' },
  { name: 'Renewable Energy Hub', status: 'planned' },
  { name: 'Outdoor Living Hub', status: 'planned' },
  { name: 'AI Home Hub', status: 'planned' },
  { name: 'Water Independence Hub', status: 'planned' },
]
